import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Contact, PressList, Client } from '@pf/shared';
import {
  useContacts,
  useCreateContact,
  useTagSuggestions,
  useBulkUpdateTags,
  useContactDuplicates,
  useMergeContacts,
  DuplicateGroup,
  CreateContactBody,
} from '../hooks/useContacts';
import { useClients } from '../hooks/useClients';
import {
  useUploadImport,
  useExecuteImport,
  useImportStatus,
} from '../hooks/useContactImport';
import {
  usePressLists,
  useBatchAddContactsToList,
} from '../hooks/usePressLists';
import { useQueryClient } from '@tanstack/react-query';
import { apiDownload } from '../lib/api';
import { buildCreateContactPayload } from '../features/contacts/createContactPayload';

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

function SourceBadge({ source }: { source: string }) {
  const colors: Record<string, string> = {
    manual: 'bg-gray-100 text-gray-700',
    reporter: 'bg-blue-100 text-blue-700',
    import: 'bg-green-100 text-green-700',
  };
  const cls = colors[source] ?? 'bg-gray-100 text-gray-700';
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${cls}`}
    >
      {source}
    </span>
  );
}

function ReporterIndicator() {
  return (
    <span title="Linked to reporter">
      <svg
        className="w-4 h-4 text-blue-500"
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path
          fillRule="evenodd"
          d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
          clipRule="evenodd"
        />
      </svg>
    </span>
  );
}

// ─── Create Contact Modal ─────────────────────────────────────────────────────

interface CreateModalProps {
  onClose: () => void;
  onSuccess: (contact: Contact) => void;
  defaultClientId?: string;
}

function CreateModal({ onClose, onSuccess, defaultClientId }: CreateModalProps) {
  const createContact = useCreateContact();
  const { data: clientsData } = useClients();
  const clients = (clientsData as unknown as { clients: Client[] } | undefined)?.clients ?? [];
  const [form, setForm] = useState<CreateContactBody>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    title: '',
    company: '',
    notes: '',
    clientId: defaultClientId ?? null,
  });
  const [error, setError] = useState('');
  const firstRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    firstRef.current?.focus();
  }, []);

  function set(field: keyof CreateContactBody, value: string) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    try {
      const body = buildCreateContactPayload(form);
      const result = await createContact.mutateAsync(body);
      onSuccess(result.data);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to create contact',
      );
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">New Contact</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-3">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label
                htmlFor="create-contact-first-name"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                First name *
              </label>
              <input
                id="create-contact-first-name"
                ref={firstRef}
                required
                value={form.firstName}
                onChange={(e) => set('firstName', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label
                htmlFor="create-contact-last-name"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Last name *
              </label>
              <input
                id="create-contact-last-name"
                required
                value={form.lastName}
                onChange={(e) => set('lastName', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          {(
            [
              { field: 'email' as const, label: 'Email', type: 'email' },
              { field: 'phone' as const, label: 'Phone', type: 'tel' },
              { field: 'title' as const, label: 'Title', type: 'text' },
              { field: 'company' as const, label: 'Company', type: 'text' },
            ] as const
          ).map(({ field, label, type }) => (
            <div key={field}>
              <label
                htmlFor={`create-contact-${field}`}
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {label}
              </label>
              <input
                id={`create-contact-${field}`}
                type={type}
                value={form[field] ?? ''}
                onChange={(e) => set(field, e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          ))}
          <div>
            <label
              htmlFor="create-contact-notes"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Notes
            </label>
            <textarea
              id="create-contact-notes"
              rows={3}
              value={form.notes ?? ''}
              onChange={(e) => set('notes', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
          </div>
          {clients.length > 0 && (
            <div>
              <label
                htmlFor="create-contact-client"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Client
              </label>
              <select
                id="create-contact-client"
                value={form.clientId ?? ''}
                onChange={(e) => setForm((prev) => ({ ...prev, clientId: e.target.value || null }))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              >
                <option value="">No client</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          )}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createContact.isPending}
              className="bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 px-4 py-2 rounded-md text-sm font-medium"
            >
              {createContact.isPending ? 'Saving...' : 'Create contact'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Add to Press List Modal ──────────────────────────────────────────────────

interface AddToListModalProps {
  selectedCount: number;
  onConfirm: (listId: string) => void;
  onClose: () => void;
}

function AddToListModal({ selectedCount, onConfirm, onClose }: AddToListModalProps) {
  const { data } = usePressLists();
  const lists = (data as unknown as { pressLists: PressList[] } | undefined)
    ?.pressLists ?? [];
  const [selectedListId, setSelectedListId] = useState('');
  const [error, setError] = useState('');

  async function handleConfirm() {
    if (!selectedListId) {
      setError('Please select a press list');
      return;
    }
    onConfirm(selectedListId);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Add to Press List</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="px-6 py-4 space-y-3">
          <p className="text-sm text-gray-600">
            Adding {selectedCount} contact{selectedCount !== 1 ? 's' : ''} to a press list.
          </p>
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Press List
            </label>
            {lists.length === 0 ? (
              <p className="text-sm text-gray-500">No press lists available.</p>
            ) : (
              <select
                value={selectedListId}
                onChange={(e) => { setSelectedListId(e.target.value); setError(''); }}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select a list...</option>
                {lists.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name} ({l.contactsCount} contacts)
                  </option>
                ))}
              </select>
            )}
          </div>
          <div className="flex justify-end gap-3 pt-1">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={!selectedListId}
              className="bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 px-4 py-2 rounded-md text-sm font-medium"
            >
              Add to List
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Bulk Tag Modal ───────────────────────────────────────────────────────────

interface BulkTagModalProps {
  selectedCount: number;
  onConfirm: (addTags: string[], removeTags: string[]) => void;
  onClose: () => void;
}

function BulkTagModal({ selectedCount, onConfirm, onClose }: BulkTagModalProps) {
  const { data: suggestionsData } = useTagSuggestions();
  const [addInput, setAddInput] = useState('');
  const [addTags, setAddTags] = useState<string[]>([]);
  const [removeTags, setRemoveTags] = useState<string[]>([]);

  const teamTags = suggestionsData?.tags.map((t) => t.tag) ?? [];

  function addPendingTag(raw: string) {
    const normalized = raw.toLowerCase().trim();
    if (!normalized || addTags.includes(normalized)) return;
    setAddTags((prev) => [...prev, normalized]);
    setAddInput('');
  }

  function removePendingAdd(tag: string) {
    setAddTags((prev) => prev.filter((t) => t !== tag));
  }

  function toggleRemove(tag: string) {
    setRemoveTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag],
    );
  }

  const canConfirm = addTags.length > 0 || removeTags.length > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Edit Tags</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="px-6 py-4 space-y-5">
          <p className="text-sm text-gray-600">
            Editing tags for {selectedCount} contact{selectedCount !== 1 ? 's' : ''}.
          </p>

          {/* Add tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Add tags
            </label>
            {addTags.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-2">
                {addTags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700"
                  >
                    {tag}
                    <button
                      onClick={() => removePendingAdd(tag)}
                      className="text-green-500 hover:text-green-700"
                    >
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </span>
                ))}
              </div>
            )}
            <input
              type="text"
              value={addInput}
              onChange={(e) => setAddInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ',') {
                  e.preventDefault();
                  if (addInput.trim()) addPendingTag(addInput);
                }
              }}
              onBlur={() => {
                if (addInput.trim()) addPendingTag(addInput);
              }}
              placeholder="Type a tag and press Enter..."
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Remove existing tags */}
          {teamTags.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Remove tags
              </label>
              <p className="text-xs text-gray-500 mb-2">
                Click tags to mark for removal
              </p>
              <div className="flex flex-wrap gap-1">
                {teamTags.map((tag) => (
                  <button
                    key={tag}
                    onClick={() => toggleRemove(tag)}
                    className={`px-2 py-0.5 rounded text-xs font-medium border transition-colors ${
                      removeTags.includes(tag)
                        ? 'bg-red-100 text-red-700 border-red-300'
                        : 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-red-50 hover:text-red-600 hover:border-red-200'
                    }`}
                  >
                    {removeTags.includes(tag) ? `✕ ${tag}` : tag}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(addTags, removeTags)}
            disabled={!canConfirm}
            className="bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 px-4 py-2 rounded-md text-sm font-medium"
          >
            Apply Tags
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Import Modal ─────────────────────────────────────────────────────────────

const CONTACT_FIELDS = [
  { value: 'first_name', label: 'First Name' },
  { value: 'last_name', label: 'Last Name' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'title', label: 'Title' },
  { value: 'company', label: 'Company' },
  { value: 'notes', label: 'Notes' },
  { value: 'tags', label: 'Tags' },
];

type ImportStep = 'upload' | 'mapping' | 'options' | 'progress';

interface ImportModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

function ImportModal({ onClose, onSuccess }: ImportModalProps) {
  const queryClient = useQueryClient();
  const [step, setStep] = useState<ImportStep>('upload');
  const [dragOver, setDragOver] = useState(false);
  const [importId, setImportId] = useState<string | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [sampleRows, setSampleRows] = useState<string[][]>([]);
  const [mappings, setMappings] = useState<Record<string, string>>({});
  const [options, setOptions] = useState({
    skipDuplicates: true,
    updateExisting: false,
    matchToReporters: false,
  });
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadMutation = useUploadImport();
  const executeMutation = useExecuteImport();
  const { data: statusData } = useImportStatus(importId, step === 'progress');

  const importStatus = statusData?.import;

  useEffect(() => {
    if (
      importStatus?.status === 'completed' ||
      importStatus?.status === 'failed'
    ) {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
    }
  }, [importStatus?.status, queryClient]);

  async function handleFile(file: File) {
    setError('');
    try {
      const result = await uploadMutation.mutateAsync(file);
      setImportId(result.importId);
      setHeaders(result.headers);
      setSampleRows(result.sampleRows);
      setMappings(result.detectedMappings);
      setStep('mapping');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    }
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }

  async function handleExecute() {
    if (!importId) return;
    setError('');
    try {
      await executeMutation.mutateAsync({ importId, mappings, options });
      setStep('progress');
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to start import',
      );
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-900">
            Import Contacts
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-6 py-4">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {step === 'upload' && (
            <div
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-colors ${
                dragOver
                  ? 'border-blue-400 bg-blue-50'
                  : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleFile(f);
                }}
              />
              <svg
                className="mx-auto h-12 w-12 text-gray-400 mb-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>
              <p className="text-sm font-medium text-gray-700">
                {uploadMutation.isPending
                  ? 'Uploading...'
                  : 'Drag and drop your file, or click to browse'}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Supports CSV and XLSX, up to 10,000 rows
              </p>
            </div>
          )}

          {step === 'mapping' && (
            <div>
              <p className="text-sm text-gray-600 mb-4">
                Map each column from your file to a contact field.
                Auto-detected mappings are pre-filled.
              </p>
              <div className="space-y-2">
                {headers.map((header) => (
                  <div key={header} className="flex items-center gap-3">
                    <span className="w-40 text-sm font-medium text-gray-700 truncate flex-shrink-0">
                      {header}
                    </span>
                    <span className="text-gray-400">→</span>
                    <select
                      value={mappings[header] ?? ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        setMappings((prev) => {
                          const next = { ...prev };
                          if (val) next[header] = val;
                          else delete next[header];
                          return next;
                        });
                      }}
                      className="flex-1 border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Skip this column</option>
                      {CONTACT_FIELDS.map((f) => (
                        <option key={f.value} value={f.value}>
                          {f.label}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
              {sampleRows.length > 0 && (
                <div className="mt-4">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                    Preview (first 5 rows)
                  </p>
                  <div className="overflow-x-auto border border-gray-200 rounded-md">
                    <table className="min-w-full text-xs">
                      <thead className="bg-gray-50">
                        <tr>
                          {headers.map((h) => (
                            <th
                              key={h}
                              className="px-2 py-1 text-left text-gray-500 font-medium"
                            >
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {sampleRows.map((row, i) => (
                          <tr key={i} className="border-t border-gray-100">
                            {row.map((cell, j) => (
                              <td
                                key={j}
                                className="px-2 py-1 text-gray-700 max-w-[150px] truncate"
                              >
                                {cell}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {step === 'options' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Configure how the import handles your data.
              </p>
              {(
                [
                  {
                    key: 'skipDuplicates',
                    label: 'Skip duplicate contacts',
                    desc: 'If a contact with the same email already exists, skip the row.',
                  },
                  {
                    key: 'updateExisting',
                    label: 'Update existing contacts',
                    desc: 'Update existing contacts with imported data (overrides skip duplicates for matching rows).',
                  },
                  {
                    key: 'matchToReporters',
                    label: 'Match to reporter database',
                    desc: 'Try to link imported contacts to reporters by email or name.',
                  },
                ] as const
              ).map(({ key, label, desc }) => (
                <label
                  key={key}
                  className="flex items-start gap-3 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={options[key]}
                    onChange={(e) =>
                      setOptions((prev) => ({
                        ...prev,
                        [key]: e.target.checked,
                      }))
                    }
                    className="mt-0.5"
                  />
                  <span>
                    <span className="block text-sm font-medium text-gray-700">
                      {label}
                    </span>
                    <span className="block text-xs text-gray-500">{desc}</span>
                  </span>
                </label>
              ))}
            </div>
          )}

          {step === 'progress' && (
            <div className="space-y-4">
              {importStatus ? (
                <>
                  <div>
                    <div className="flex justify-between text-sm text-gray-600 mb-1">
                      <span>
                        {importStatus.status === 'completed'
                          ? 'Complete'
                          : importStatus.status === 'failed'
                            ? 'Failed'
                            : 'Processing...'}
                      </span>
                      <span>{importStatus.progress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all ${
                          importStatus.status === 'failed'
                            ? 'bg-red-500'
                            : 'bg-blue-600'
                        }`}
                        style={{ width: `${importStatus.progress}%` }}
                      />
                    </div>
                  </div>
                  {(importStatus.status === 'completed' ||
                    importStatus.status === 'failed') && (
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      {(
                        [
                          {
                            label: 'Created',
                            value: importStatus.createdCount,
                            color: 'text-green-700 bg-green-50',
                          },
                          {
                            label: 'Updated',
                            value: importStatus.updatedCount,
                            color: 'text-blue-700 bg-blue-50',
                          },
                          {
                            label: 'Skipped',
                            value: importStatus.skippedCount,
                            color: 'text-gray-700 bg-gray-50',
                          },
                          {
                            label: 'Failed',
                            value: importStatus.failedCount,
                            color: 'text-red-700 bg-red-50',
                          },
                        ] as const
                      ).map(({ label, value, color }) => (
                        <div key={label} className={`p-3 rounded-lg ${color}`}>
                          <div className="text-2xl font-bold">{value}</div>
                          <div className="text-xs">{label}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  {importStatus.errors && importStatus.errors.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-1">
                        Row errors ({importStatus.errors.length})
                      </p>
                      <div className="max-h-32 overflow-y-auto border border-gray-200 rounded-md p-2 space-y-1">
                        {importStatus.errors
                          .slice(0, 20)
                          .map(
                            (
                              e: { row: number; reason: string },
                              i: number,
                            ) => (
                              <p key={i} className="text-xs text-red-600">
                                Row {e.row}: {e.reason}
                              </p>
                            ),
                          )}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-sm text-gray-500">Starting import...</p>
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-between flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            {importStatus?.status === 'completed' ? 'Close' : 'Cancel'}
          </button>
          <div className="flex gap-2">
            {step === 'mapping' && (
              <button
                onClick={() => setStep('options')}
                className="bg-blue-600 text-white hover:bg-blue-700 px-4 py-2 rounded-md text-sm font-medium"
              >
                Next: Options
              </button>
            )}
            {step === 'options' && (
              <>
                <button
                  onClick={() => setStep('mapping')}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Back
                </button>
                <button
                  onClick={handleExecute}
                  disabled={executeMutation.isPending}
                  className="bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 px-4 py-2 rounded-md text-sm font-medium"
                >
                  {executeMutation.isPending ? 'Starting...' : 'Start Import'}
                </button>
              </>
            )}
            {step === 'progress' && importStatus?.status === 'completed' && (
              <button
                onClick={onSuccess}
                className="bg-blue-600 text-white hover:bg-blue-700 px-4 py-2 rounded-md text-sm font-medium"
              >
                View Contacts
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Review Duplicates Modal ──────────────────────────────────────────────────

function ReviewDuplicatesModal({ onClose }: { onClose: () => void }) {
  const { data, isLoading } = useContactDuplicates();
  const mergeMutation = useMergeContacts();
  const [primaryIds, setPrimaryIds] = useState<Record<string, string>>({});
  const [resolvedEmails, setResolvedEmails] = useState<Set<string>>(new Set());
  const [mergingEmail, setMergingEmail] = useState<string | null>(null);
  const [error, setError] = useState('');

  const groups: DuplicateGroup[] = data?.groups ?? [];

  function getGroupEmail(group: DuplicateGroup): string {
    return (group.contacts[0].email ?? '').toLowerCase();
  }

  function getPrimaryId(group: DuplicateGroup): string {
    const email = getGroupEmail(group);
    // Default to first contact (oldest — backend sorts by createdAt asc)
    return primaryIds[email] ?? group.contacts[0].id;
  }

  async function handleMerge(group: DuplicateGroup) {
    const email = getGroupEmail(group);
    const primaryId = getPrimaryId(group);
    const mergeIds = group.contacts
      .filter((c) => c.id !== primaryId)
      .map((c) => c.id);

    setError('');
    setMergingEmail(email);
    try {
      await mergeMutation.mutateAsync({
        primaryContactId: primaryId,
        mergeContactIds: mergeIds,
      });
      setResolvedEmails((prev) => new Set([...prev, email]));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Merge failed');
    } finally {
      setMergingEmail(null);
    }
  }

  const activeGroups = groups.filter(
    (g) => !resolvedEmails.has(getGroupEmail(g)),
  );
  const resolvedCount = resolvedEmails.size;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Review Duplicates
            </h2>
            {!isLoading && groups.length > 0 && (
              <p className="text-sm text-gray-500 mt-0.5">
                {activeGroups.length} group
                {activeGroups.length !== 1 ? 's' : ''} remaining
                {resolvedCount > 0 && ` · ${resolvedCount} resolved`}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-6 py-4 space-y-4">
          {isLoading && (
            <p className="text-sm text-gray-500 text-center py-6">
              Scanning for duplicates…
            </p>
          )}

          {!isLoading && groups.length === 0 && (
            <div className="text-center py-10">
              <svg
                className="mx-auto h-10 w-10 text-green-400 mb-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p className="text-sm font-medium text-gray-700">
                No duplicates found
              </p>
              <p className="text-xs text-gray-500 mt-1">
                All contacts have unique email addresses.
              </p>
            </div>
          )}

          {!isLoading && groups.length > 0 && activeGroups.length === 0 && (
            <div className="text-center py-10">
              <svg
                className="mx-auto h-10 w-10 text-green-400 mb-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p className="text-sm font-medium text-gray-700">
                All duplicates resolved
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Merged {resolvedCount} group{resolvedCount !== 1 ? 's' : ''}.
              </p>
            </div>
          )}

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {activeGroups.map((group) => {
            const email = getGroupEmail(group);
            const primaryId = getPrimaryId(group);
            const nonPrimary = group.contacts.filter((c) => c.id !== primaryId);
            const totalListsTransferred = nonPrimary.reduce(
              (sum, c) => sum + c.pressListCount,
              0,
            );
            const primary = group.contacts.find((c) => c.id === primaryId)!;
            const isMerging = mergingEmail === email;

            return (
              <div
                key={email}
                className="border border-gray-200 rounded-lg p-4 space-y-3"
              >
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Shared email: {email}
                </p>

                <div
                  className={`grid gap-3 ${group.contacts.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}
                >
                  {group.contacts.map((contact) => {
                    const isSelected = contact.id === primaryId;
                    return (
                      <label
                        key={contact.id}
                        className={`flex flex-col p-3 border rounded-lg cursor-pointer transition-colors ${
                          isSelected
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-start gap-2">
                          <input
                            type="radio"
                            name={`primary-${email}`}
                            value={contact.id}
                            checked={isSelected}
                            onChange={() =>
                              setPrimaryIds((prev) => ({
                                ...prev,
                                [email]: contact.id,
                              }))
                            }
                            className="mt-0.5 flex-shrink-0"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {contact.firstName} {contact.lastName}
                            </p>
                            {contact.company && (
                              <p className="text-xs text-gray-500 truncate">
                                {contact.company}
                              </p>
                            )}
                            <div className="mt-1.5 flex flex-wrap gap-1">
                              <SourceBadge source={contact.source} />
                              {contact.pressListCount > 0 && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
                                  {contact.pressListCount} list
                                  {contact.pressListCount !== 1 ? 's' : ''}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        {isSelected && (
                          <p className="mt-2 text-xs text-blue-600 font-medium">
                            Primary (kept)
                          </p>
                        )}
                      </label>
                    );
                  })}
                </div>

                {totalListsTransferred > 0 && (
                  <p className="text-xs text-gray-600">
                    {totalListsTransferred} press list membership
                    {totalListsTransferred !== 1 ? 's' : ''} will be
                    transferred to {primary.firstName} {primary.lastName}.
                  </p>
                )}

                <button
                  onClick={() => handleMerge(group)}
                  disabled={isMerging}
                  className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 font-medium"
                >
                  {isMerging ? 'Merging…' : 'Merge'}
                </button>
              </div>
            );
          })}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Filter Chip ─────────────────────────────────────────────────────────────

function FilterChip({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
      {label}
      <button
        onClick={onRemove}
        className="ml-0.5 text-blue-500 hover:text-blue-700"
        aria-label={`Remove filter: ${label}`}
      >
        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </button>
    </span>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function ContactsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showCreate, setShowCreate] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showAddToList, setShowAddToList] = useState(false);
  const [showBulkTag, setShowBulkTag] = useState(false);
  const [showReviewDuplicates, setShowReviewDuplicates] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [batchError, setBatchError] = useState('');
  const [batchSuccess, setBatchSuccess] = useState('');
  const [exporting, setExporting] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportRef = useRef<HTMLDivElement>(null);

  // Read filter state from URL params
  const page = Math.max(1, parseInt(searchParams.get('page') ?? '1', 10));
  const urlSearch = searchParams.get('q') ?? '';
  const source = searchParams.get('source') ?? '';
  const hasReporterParam = searchParams.get('hasReporter') ?? '';
  const tagsParam = searchParams.get('tags') ?? '';
  const pressListIdParam = searchParams.get('pressListId') ?? '';
  const clientIdParam = searchParams.get('clientId') ?? '';
  const companyParam = searchParams.get('company') ?? '';
  const outreachStatusParam = searchParams.get('outreachStatus') ?? '';
  const sortByParam = (searchParams.get('sortBy') ?? 'createdAt') as 'name' | 'lastOutreach' | 'createdAt';
  const sortOrderParam = (searchParams.get('sortOrder') ?? 'desc') as 'asc' | 'desc';
  const staleDaysParam = searchParams.get('staleDays') ?? '';

  const hasReporter =
    hasReporterParam === 'true' ? true : hasReporterParam === 'false' ? false : undefined;

  // Local state for debounced text inputs
  const [searchInput, setSearchInput] = useState(urlSearch);
  const [companyInput, setCompanyInput] = useState(companyParam);
  const [tagInput, setTagInput] = useState('');
  const debouncedSearch = useDebounce(searchInput, 300);
  const debouncedCompany = useDebounce(companyInput, 300);

  const batchAdd = useBatchAddContactsToList();
  const bulkUpdateTags = useBulkUpdateTags();
  const { data: pressListsData } = usePressLists();
  const pressListOptions =
    (pressListsData as unknown as { pressLists: PressList[] } | undefined)?.pressLists ?? [];
  const { data: clientsData } = useClients();
  const clientOptions =
    (clientsData as unknown as { clients: Client[] } | undefined)?.clients ?? [];

  // Sync debounced search to URL
  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (debouncedSearch) next.set('q', debouncedSearch);
        else next.delete('q');
        next.delete('page');
        return next;
      },
      { replace: true },
    );
  }, [debouncedSearch]);

  // Sync debounced company to URL
  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (debouncedCompany) next.set('company', debouncedCompany);
        else next.delete('company');
        next.delete('page');
        return next;
      },
      { replace: true },
    );
  }, [debouncedCompany]);

  function setFilter(key: string, value: string | undefined) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        next.delete('page');
        return next;
      },
      { replace: true },
    );
  }

  function setPageParam(newPage: number) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (newPage > 1) next.set('page', String(newPage));
        else next.delete('page');
        return next;
      },
      { replace: true },
    );
  }

  function addTag(tag: string) {
    const trimmed = tag.trim().toLowerCase();
    if (!trimmed) return;
    const current = tagsParam ? tagsParam.split(',').filter(Boolean) : [];
    if (!current.includes(trimmed)) {
      setFilter('tags', [...current, trimmed].join(','));
    }
    setTagInput('');
  }

  function removeTag(tag: string) {
    const current = tagsParam.split(',').filter((t) => t.trim() && t.trim() !== tag);
    setFilter('tags', current.join(',') || undefined);
  }

  const { data, isLoading, isError } = useContacts({
    page,
    limit: 20,
    search: urlSearch || undefined,
    source: source || undefined,
    hasReporter,
    tags: tagsParam || undefined,
    pressListId: pressListIdParam || undefined,
    clientId: clientIdParam || undefined,
    company: companyParam || undefined,
    outreachStatus: (outreachStatusParam as 'pitched' | 'replied' | 'no_response' | 'bounced') || undefined,
    sortBy: sortByParam,
    sortOrder: sortOrderParam,
    staleDays: staleDaysParam ? parseInt(staleDaysParam, 10) : undefined,
  });

  const contacts = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / 20);

  const allVisibleSelected =
    contacts.length > 0 && contacts.every((c) => selectedIds.has(c.id));

  // Active filter chips (excluding search which has its own input)
  const activeFilters: Array<{ label: string; onRemove: () => void }> = [];
  if (source) {
    activeFilters.push({
      label: `Source: ${source}`,
      onRemove: () => setFilter('source', undefined),
    });
  }
  if (hasReporterParam) {
    activeFilters.push({
      label: hasReporter ? 'Has reporter' : 'No reporter',
      onRemove: () => setFilter('hasReporter', undefined),
    });
  }
  if (companyParam) {
    activeFilters.push({
      label: `Company: ${companyParam}`,
      onRemove: () => {
        setFilter('company', undefined);
        setCompanyInput('');
      },
    });
  }
  if (pressListIdParam) {
    const listName = pressListOptions.find((l) => l.id === pressListIdParam)?.name ?? 'List';
    activeFilters.push({
      label: `List: ${listName}`,
      onRemove: () => setFilter('pressListId', undefined),
    });
  }
  if (clientIdParam) {
    const clientName = clientOptions.find((c) => c.id === clientIdParam)?.name ?? 'Client';
    activeFilters.push({
      label: `Client: ${clientName}`,
      onRemove: () => setFilter('clientId', undefined),
    });
  }
  if (tagsParam) {
    tagsParam
      .split(',')
      .filter(Boolean)
      .forEach((tag) => {
        activeFilters.push({ label: `Tag: ${tag}`, onRemove: () => removeTag(tag) });
      });
  }
  if (outreachStatusParam) {
    const labels: Record<string, string> = {
      pitched: 'Outreach: Pitched',
      replied: 'Outreach: Replied',
      no_response: 'Outreach: No response',
      bounced: 'Outreach: Bounced',
    };
    activeFilters.push({
      label: labels[outreachStatusParam] ?? `Outreach: ${outreachStatusParam}`,
      onRemove: () => setFilter('outreachStatus', undefined),
    });
  }
  if (staleDaysParam) {
    activeFilters.push({
      label: `Stale: ${staleDaysParam}+ days`,
      onRemove: () => setFilter('staleDays', undefined),
    });
  }

  function toggleSelect(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setBatchError('');
    setBatchSuccess('');
  }

  function toggleSelectAll() {
    if (allVisibleSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        contacts.forEach((c) => next.delete(c.id));
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        contacts.forEach((c) => next.add(c.id));
        return next;
      });
    }
    setBatchError('');
    setBatchSuccess('');
  }

  function clearSelection() {
    setSelectedIds(new Set());
    setBatchError('');
    setBatchSuccess('');
  }

  async function handleBatchAddToList(listId: string) {
    setBatchError('');
    setBatchSuccess('');
    try {
      const result = await batchAdd.mutateAsync({
        listId,
        contactIds: Array.from(selectedIds),
      });
      setBatchSuccess(
        `Added ${result.added} contact${result.added !== 1 ? 's' : ''}${
          result.skipped > 0 ? ` (${result.skipped} already on list)` : ''
        }.`,
      );
      setSelectedIds(new Set());
    } catch (err) {
      setBatchError(err instanceof Error ? err.message : 'Failed to add contacts');
    }
    setShowAddToList(false);
  }

  async function handleBulkTag(addTags: string[], removeTags: string[]) {
    setBatchError('');
    setBatchSuccess('');
    setShowBulkTag(false);
    try {
      const result = await bulkUpdateTags.mutateAsync({
        contactIds: Array.from(selectedIds),
        addTags,
        removeTags,
      });
      setBatchSuccess(
        `Updated tags on ${result.updated} contact${result.updated !== 1 ? 's' : ''}.`,
      );
      setSelectedIds(new Set());
    } catch (err) {
      setBatchError(err instanceof Error ? err.message : 'Failed to update tags');
    }
  }

  function handleCreated(contact: Contact) {
    setShowCreate(false);
    navigate(`/contacts/${contact.id}`);
  }

  // Close export menu on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (exportRef.current && !exportRef.current.contains(e.target as Node)) {
        setShowExportMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  async function handleExport(format: 'csv' | 'xlsx') {
    setExporting(true);
    setShowExportMenu(false);
    try {
      await apiDownload(
        '/contacts/export',
        {
          format,
          search: urlSearch || undefined,
          source: source || undefined,
          hasReporter: hasReporterParam || undefined,
          tags: tagsParam || undefined,
          pressListId: pressListIdParam || undefined,
          clientId: clientIdParam || undefined,
          company: companyParam || undefined,
        },
        `contacts.${format}`,
      );
    } finally {
      setExporting(false);
    }
  }

  const hasSelection = selectedIds.size > 0;
  const hasActiveFilters = activeFilters.length > 0;

  return (
    <div className="p-6">
      {showCreate && (
        <CreateModal
          onClose={() => setShowCreate(false)}
          onSuccess={handleCreated}
          defaultClientId={clientIdParam || undefined}
        />
      )}
      {showImport && (
        <ImportModal
          onClose={() => setShowImport(false)}
          onSuccess={() => {
            setShowImport(false);
            setPageParam(1);
          }}
        />
      )}
      {showAddToList && (
        <AddToListModal
          selectedCount={selectedIds.size}
          onConfirm={handleBatchAddToList}
          onClose={() => setShowAddToList(false)}
        />
      )}
      {showBulkTag && (
        <BulkTagModal
          selectedCount={selectedIds.size}
          onConfirm={handleBulkTag}
          onClose={() => setShowBulkTag(false)}
        />
      )}
      {showReviewDuplicates && (
        <ReviewDuplicatesModal onClose={() => setShowReviewDuplicates(false)} />
      )}

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Contacts</h1>
          {total > 0 && <p className="mt-1 text-sm text-gray-500">{total} total</p>}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowReviewDuplicates(true)}
            className="bg-white text-gray-700 hover:bg-gray-50 border border-gray-300 px-4 py-2 rounded-md text-sm font-medium"
          >
            Find Duplicates
          </button>

          {/* Export dropdown */}
          <div ref={exportRef} className="relative">
            <button
              onClick={() => setShowExportMenu((v) => !v)}
              disabled={exporting}
              className="bg-white text-gray-700 hover:bg-gray-50 border border-gray-300 px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50"
            >
              {exporting ? 'Exporting...' : 'Export'}
            </button>
            {showExportMenu && (
              <div className="absolute right-0 mt-1 w-40 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                <button
                  onClick={() => handleExport('csv')}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 first:rounded-t-lg"
                >
                  Export as CSV
                </button>
                <button
                  onClick={() => handleExport('xlsx')}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 last:rounded-b-lg"
                >
                  Export as XLSX
                </button>
              </div>
            )}
          </div>

          <button
            onClick={() => setShowImport(true)}
            className="bg-white text-gray-700 hover:bg-gray-50 border border-gray-300 px-4 py-2 rounded-md text-sm font-medium"
          >
            Import
          </button>
          <button
            onClick={() => setShowCreate(true)}
            className="bg-blue-600 text-white hover:bg-blue-700 px-4 py-2 rounded-md text-sm font-medium"
          >
            New Contact
          </button>
        </div>
      </div>

      {/* Selection toolbar */}
      {hasSelection && (
        <div className="mb-4 flex items-center gap-3 px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg">
          <span className="text-sm font-medium text-blue-800">{selectedIds.size} selected</span>
          <button
            onClick={() => setShowAddToList(true)}
            className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium"
          >
            Add to Press List
          </button>
          <button
            onClick={() => setShowBulkTag(true)}
            className="px-3 py-1.5 text-sm bg-white text-blue-700 border border-blue-300 rounded-md hover:bg-blue-50 font-medium"
          >
            Edit Tags
          </button>
          <button onClick={clearSelection} className="px-3 py-1.5 text-sm text-blue-700 hover:text-blue-900">
            Clear selection
          </button>
          {batchError && <span className="text-sm text-red-600">{batchError}</span>}
          {batchSuccess && <span className="text-sm text-green-700">{batchSuccess}</span>}
        </div>
      )}

      <div className="bg-white rounded-lg shadow">
        {/* Filter bar */}
        <div className="px-4 py-3 border-b border-gray-200 space-y-2">
          <div className="flex flex-wrap gap-2">
            {/* Search */}
            <input
              type="search"
              placeholder="Search by name, email, company, or notes..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="flex-1 min-w-[200px] max-w-sm border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />

            {/* Source filter */}
            <select
              value={source}
              onChange={(e) => setFilter('source', e.target.value || undefined)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            >
              <option value="">All sources</option>
              <option value="manual">Manual</option>
              <option value="import">Import</option>
              <option value="reporter_db">Reporter DB</option>
            </select>

            {/* Reporter filter */}
            <select
              value={hasReporterParam}
              onChange={(e) => setFilter('hasReporter', e.target.value || undefined)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            >
              <option value="">All contacts</option>
              <option value="true">Has reporter</option>
              <option value="false">No reporter</option>
            </select>

            {/* Company filter */}
            <input
              type="text"
              placeholder="Filter by company..."
              value={companyInput}
              onChange={(e) => setCompanyInput(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-44"
            />

            {/* Press list filter */}
            {pressListOptions.length > 0 && (
              <select
                value={pressListIdParam}
                onChange={(e) => setFilter('pressListId', e.target.value || undefined)}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              >
                <option value="">All lists</option>
                {pressListOptions.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
              </select>
            )}

            {/* Client filter */}
            {clientOptions.length > 0 && (
              <select
                value={clientIdParam}
                onChange={(e) => setFilter('clientId', e.target.value || undefined)}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              >
                <option value="">All clients</option>
                {clientOptions.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            )}

            {/* Tag input */}
            <input
              type="text"
              placeholder="Add tag filter..."
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ',') {
                  e.preventDefault();
                  addTag(tagInput);
                }
              }}
              onBlur={() => {
                if (tagInput.trim()) addTag(tagInput);
              }}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-36"
            />

            {/* Outreach status filter */}
            <select
              value={outreachStatusParam}
              onChange={(e) => setFilter('outreachStatus', e.target.value || undefined)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            >
              <option value="">Any outreach</option>
              <option value="pitched">Pitched</option>
              <option value="replied">Replied</option>
              <option value="no_response">No response</option>
              <option value="bounced">Bounced</option>
            </select>

            {/* Sort controls */}
            <select
              value={sortByParam}
              onChange={(e) => setFilter('sortBy', e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            >
              <option value="createdAt">Sort: Added</option>
              <option value="name">Sort: Name</option>
              <option value="lastOutreach">Sort: Last pitched</option>
            </select>
            <button
              onClick={() => setFilter('sortOrder', sortOrderParam === 'asc' ? 'desc' : 'asc')}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              title={sortOrderParam === 'asc' ? 'Ascending' : 'Descending'}
            >
              {sortOrderParam === 'asc' ? '↑' : '↓'}
            </button>

            {/* Stale contacts filter */}
            <input
              type="number"
              min="1"
              max="365"
              placeholder="Stale (days)"
              value={staleDaysParam}
              onChange={(e) => setFilter('staleDays', e.target.value || undefined)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-32"
              title="Show contacts not pitched in this many days"
            />
          </div>

          {/* Active filter chips */}
          {hasActiveFilters && (
            <div className="flex flex-wrap gap-1.5 pt-1">
              {activeFilters.map((f) => (
                <FilterChip key={f.label} label={f.label} onRemove={f.onRemove} />
              ))}
              <button
                onClick={() => {
                  setSearchParams({}, { replace: true });
                  setSearchInput('');
                  setCompanyInput('');
                  setTagInput('');
                }}
                className="text-xs text-gray-500 hover:text-gray-700 underline ml-1"
              >
                Clear all
              </button>
            </div>
          )}
        </div>

        {isLoading && (
          <div className="px-6 py-10 text-center text-sm text-gray-500">Loading...</div>
        )}
        {isError && (
          <div className="px-6 py-10 text-center text-sm text-red-600">
            Failed to load contacts.
          </div>
        )}
        {!isLoading && !isError && contacts.length === 0 && (
          <div className="px-6 py-10 text-center text-sm text-gray-500">
            {urlSearch || hasActiveFilters
              ? 'No contacts match your search or filters.'
              : 'No contacts yet. Create your first one.'}
          </div>
        )}
        {!isLoading && contacts.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={allVisibleSelected}
                      onChange={toggleSelectAll}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      title="Select all on this page"
                    />
                  </th>
                  {['Name', 'Email', 'Company', 'Title', 'Source', 'Tags', ''].map((h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {contacts.map((c) => (
                  <tr
                    key={c.id}
                    className={`hover:bg-gray-50 transition-colors ${
                      selectedIds.has(c.id) ? 'bg-blue-50' : ''
                    }`}
                  >
                    <td className="px-4 py-3 w-10" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(c.id)}
                        onChange={() => toggleSelect(c.id)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </td>
                    <td
                      className="px-4 py-3 text-sm font-medium text-gray-900 cursor-pointer"
                      onClick={() => navigate(`/contacts/${c.id}`)}
                    >
                      {c.firstName} {c.lastName}
                    </td>
                    <td
                      className="px-4 py-3 text-sm text-gray-500 cursor-pointer"
                      onClick={() => navigate(`/contacts/${c.id}`)}
                    >
                      {c.email ?? '—'}
                    </td>
                    <td
                      className="px-4 py-3 text-sm text-gray-500 cursor-pointer"
                      onClick={() => navigate(`/contacts/${c.id}`)}
                    >
                      {c.company ?? '—'}
                    </td>
                    <td
                      className="px-4 py-3 text-sm text-gray-500 cursor-pointer"
                      onClick={() => navigate(`/contacts/${c.id}`)}
                    >
                      {c.title ?? '—'}
                    </td>
                    <td
                      className="px-4 py-3 cursor-pointer"
                      onClick={() => navigate(`/contacts/${c.id}`)}
                    >
                      <SourceBadge source={c.source} />
                    </td>
                    <td
                      className="px-4 py-3 cursor-pointer max-w-[180px]"
                      onClick={() => navigate(`/contacts/${c.id}`)}
                    >
                      {c.tags && c.tags.length > 0 && (
                        <div className="flex flex-wrap gap-0.5">
                          {c.tags.slice(0, 2).map((tag) => (
                            <span
                              key={tag}
                              className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600"
                            >
                              {tag}
                            </span>
                          ))}
                          {c.tags.length > 2 && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs text-gray-400">
                              +{c.tags.length - 2}
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                    <td
                      className="px-4 py-3 cursor-pointer"
                      onClick={() => navigate(`/contacts/${c.id}`)}
                    >
                      {c.reporterId && <ReporterIndicator />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Page {page} of {totalPages}
            </p>
            <div className="flex gap-2">
              <button
                disabled={page <= 1}
                onClick={() => setPageParam(page - 1)}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-40 hover:bg-gray-50"
              >
                Previous
              </button>
              <button
                disabled={page >= totalPages}
                onClick={() => setPageParam(page + 1)}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-40 hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
